import { useMemo, useState } from "react";

import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";

import {
  FilterCard,
  JsonBlock,
  SegmentedControl,
  Select,
  StatusPill,
  TableEmptyRow,
  TableShell,
} from "@/components/common";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNullableCompactDateTimeWithSeconds } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type Publication = RouterOutputs["admin"]["instagramDebug"]["publications"]["list"][number];
type MediaSync = RouterOutputs["admin"]["instagramDebug"]["mediaSyncs"]["list"][number];
type DebugEvent = RouterOutputs["admin"]["instagramDebug"]["publications"]["get"]["events"][number];

const PUBLICATION_STATUSES = [
  "draft",
  "ready_for_publish",
  "publishing",
  "published",
  "publish_failed",
] as const;
const MEDIA_SYNC_STATUSES = ["queued", "processing", "done", "failed"] as const;

const formatDate = formatNullableCompactDateTimeWithSeconds;

const EventTimeline = ({ events }: { events: DebugEvent[] }) => {
  if (events.length === 0) {
    return <div className="text-sm text-muted-foreground">No diagnostic events recorded.</div>;
  }

  return (
    <div className="space-y-2">
      {events.map((event) => {
        const phase =
          typeof event.dataJson?.phase === "string" ? event.dataJson.phase : event.eventType;
        return (
          <div key={event.id} className="rounded-md border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-[11px] uppercase">
                  {phase}
                </Badge>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {event.eventType}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">{formatDate(event.at)}</span>
            </div>
            {event.message ? <p className="mt-2 text-sm">{event.message}</p> : null}
            {event.dataJson && Object.keys(event.dataJson).length > 0 ? (
              <div className="mt-3">
                <JsonBlock value={event.dataJson} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const WorkerDebugPanel = ({
  workerJob,
  runs,
  events,
}: {
  workerJob: RouterOutputs["admin"]["instagramDebug"]["publications"]["get"]["workerJob"];
  runs: RouterOutputs["admin"]["instagramDebug"]["publications"]["get"]["runs"];
  events: DebugEvent[];
}) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Worker Job</CardTitle>
      </CardHeader>
      <CardContent>
        {workerJob ? (
          <div className="grid gap-3 text-sm md:grid-cols-3">
            <div>
              <div className="text-muted-foreground">Status</div>
              <StatusPill label={workerJob.status} />
            </div>
            <div>
              <div className="text-muted-foreground">Attempts</div>
              <div className="font-mono">
                {workerJob.attemptCount}/{workerJob.maxAttempts}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Next Run</div>
              <div>{formatDate(workerJob.nextRunAt)}</div>
            </div>
            <div className="md:col-span-3">
              <div className="text-muted-foreground">Last Error</div>
              <div className="text-destructive">{workerJob.lastError ?? "-"}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No worker job found.</div>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Runs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {runs.length > 0 ? (
          runs.map((run) => (
            <div key={run.id} className="rounded-md border p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <StatusPill label={run.status} />
                <span className="text-xs text-muted-foreground">{formatDate(run.claimedAt)}</span>
              </div>
              <div className="mt-2 grid gap-2 md:grid-cols-3">
                <span className="font-mono text-xs">Attempt #{run.attemptNumber}</span>
                <span className="font-mono text-xs">Duration {run.durationMs ?? "-"}ms</span>
                <span className="font-mono text-xs">Lease {formatDate(run.leaseExpiresAt)}</span>
              </div>
              {run.errorMessage ? (
                <p className="mt-2 text-xs text-destructive">{run.errorMessage}</p>
              ) : null}
            </div>
          ))
        ) : (
          <div className="text-sm text-muted-foreground">No runs recorded.</div>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Diagnostic Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <EventTimeline events={events} />
      </CardContent>
    </Card>
  </div>
);

const PublicationTable = ({
  rows,
  onSelect,
}: {
  rows: Publication[];
  onSelect: (id: string) => void;
}) => (
  <TableShell>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Updated</TableHead>
          <TableHead>Brand</TableHead>
          <TableHead>Campaign</TableHead>
          <TableHead>Post Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Error</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length > 0 ? (
          rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="text-xs text-muted-foreground">
                {formatDate(row.updatedAt)}
              </TableCell>
              <TableCell>
                <div className="text-sm font-medium">{row.sourceDomain}</div>
                <div className="font-mono text-[11px] text-muted-foreground">{row.brandId}</div>
              </TableCell>
              <TableCell className="text-sm">{row.campaignName}</TableCell>
              <TableCell className="font-mono text-xs">{row.postType}</TableCell>
              <TableCell>
                <StatusPill label={row.status} />
              </TableCell>
              <TableCell className="max-w-[320px] truncate text-xs text-destructive">
                {row.lastError ?? "-"}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => onSelect(row.id)}>
                  Inspect
                </Button>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableEmptyRow colSpan={7}>No publications found.</TableEmptyRow>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableShell>
);

const MediaSyncTable = ({
  rows,
  onSelect,
}: {
  rows: MediaSync[];
  onSelect: (id: string) => void;
}) => (
  <TableShell>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Updated</TableHead>
          <TableHead>Account</TableHead>
          <TableHead>Imported</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Error</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length > 0 ? (
          rows.map((row) => (
            <TableRow key={row.connectionId}>
              <TableCell className="text-xs text-muted-foreground">
                {formatDate(row.updatedAt)}
              </TableCell>
              <TableCell>
                <div className="text-sm font-medium">{row.accountName ?? "Instagram account"}</div>
                <div className="font-mono text-[11px] text-muted-foreground">
                  {row.instagramAccountId ?? row.connectionId}
                </div>
              </TableCell>
              <TableCell className="font-mono text-xs">{row.importedCount}</TableCell>
              <TableCell>
                <StatusPill label={row.syncStatus} />
              </TableCell>
              <TableCell className="max-w-[380px] truncate text-xs text-destructive">
                {row.syncLastError ?? row.connectionLastError ?? "-"}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => onSelect(row.connectionId)}>
                  Inspect
                </Button>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableEmptyRow colSpan={6}>No sync records found.</TableEmptyRow>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableShell>
);

export function InstagramDebugPage() {
  const LIMIT = 50;
  const [tab, setTab] = useState<"publishing" | "sync">("publishing");
  const [publicationStatus, setPublicationStatus] = useState<
    "" | (typeof PUBLICATION_STATUSES)[number]
  >("");
  const [syncStatus, setSyncStatus] = useState<"" | (typeof MEDIA_SYNC_STATUSES)[number]>("");
  const [brandId, setBrandId] = useState("");
  const [selectedPublicationId, setSelectedPublicationId] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  const publicationInput = useMemo(
    () => ({
      limit: LIMIT,
      offset: 0,
      brandId: brandId.trim() ? brandId.trim() : undefined,
      status: publicationStatus || undefined,
    }),
    [brandId, publicationStatus],
  );
  const syncInput = useMemo(
    () => ({ limit: LIMIT, offset: 0, status: syncStatus || undefined }),
    [syncStatus],
  );

  const publicationsQuery = trpc.admin.instagramDebug.publications.list.useQuery(publicationInput);
  const syncsQuery = trpc.admin.instagramDebug.mediaSyncs.list.useQuery(syncInput);
  const publicationDetailQuery = trpc.admin.instagramDebug.publications.get.useQuery(
    { publicationId: selectedPublicationId ?? "" },
    { enabled: selectedPublicationId !== null },
  );
  const syncDetailQuery = trpc.admin.instagramDebug.mediaSyncs.get.useQuery(
    { connectionId: selectedConnectionId ?? "" },
    { enabled: selectedConnectionId !== null },
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Instagram Debug</h2>
          <p className="text-sm text-muted-foreground">
            Inspect publish requests, rendered-image preflight checks, media sync progress, and
            worker deadlocks.
          </p>
        </div>
        <SegmentedControl
          options={[
            { value: "publishing", label: "Publishing" },
            { value: "sync", label: "Media Sync" },
          ]}
          value={tab}
          onChange={setTab}
          className="shrink-0"
        />
      </div>

      {tab === "publishing" ? (
        <div className="space-y-4">
          <FilterCard title="Publishing Filters">
            <Input
              placeholder="Brand ID"
              value={brandId}
              onChange={(event) => setBrandId(event.target.value)}
            />
            <Select
              value={publicationStatus}
              onChange={(event) =>
                setPublicationStatus((event.target.value as typeof publicationStatus) || "")
              }
            >
              <option value="">All publication statuses</option>
              {PUBLICATION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </FilterCard>

          {publicationsQuery.isPending ? (
            <Skeleton className="h-72 w-full" />
          ) : publicationsQuery.error || !publicationsQuery.data ? (
            <div className="rounded-md border p-6 text-sm text-destructive">
              Failed to load publications.
            </div>
          ) : (
            <PublicationTable rows={publicationsQuery.data} onSelect={setSelectedPublicationId} />
          )}

          {selectedPublicationId ? (
            publicationDetailQuery.isPending ? (
              <Skeleton className="h-96 w-full" />
            ) : publicationDetailQuery.data ? (
              <WorkerDebugPanel
                workerJob={publicationDetailQuery.data.workerJob}
                runs={publicationDetailQuery.data.runs}
                events={publicationDetailQuery.data.events}
              />
            ) : null
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <FilterCard title="Media Sync Filters">
            <Select
              value={syncStatus}
              onChange={(event) => setSyncStatus((event.target.value as typeof syncStatus) || "")}
            >
              <option value="">All sync statuses</option>
              {MEDIA_SYNC_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </FilterCard>

          {syncsQuery.isPending ? (
            <Skeleton className="h-72 w-full" />
          ) : syncsQuery.error || !syncsQuery.data ? (
            <div className="rounded-md border p-6 text-sm text-destructive">
              Failed to load sync records.
            </div>
          ) : (
            <MediaSyncTable rows={syncsQuery.data} onSelect={setSelectedConnectionId} />
          )}

          {selectedConnectionId ? (
            syncDetailQuery.isPending ? (
              <Skeleton className="h-96 w-full" />
            ) : syncDetailQuery.data ? (
              <WorkerDebugPanel
                workerJob={syncDetailQuery.data.workerJob}
                runs={syncDetailQuery.data.runs}
                events={syncDetailQuery.data.events}
              />
            ) : null
          ) : null}
        </div>
      )}
    </div>
  );
}
